import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { LayerManagerProvider } from '../internals/LayerManager';
import { createTheme } from './createTheme';
import { defaultBrand } from './defaultBrand';
import type { Appearance, BrandConfig, Density, ThemeContextValue } from './types';

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  /** Merged with `defaultBrand`. */
  brand?: Partial<BrandConfig>;
  appearance?: Appearance;
  density?: Density;
  children: React.ReactNode;
}

export function ThemeProvider({
  brand: brandProp,
  appearance: appearanceProp,
  density: densityProp,
  children,
}: ThemeProviderProps) {
  const [brand, setBrandState] = useState<BrandConfig>(() => ({
    ...defaultBrand,
    ...brandProp,
  }));
  const [appearance, setAppearanceState] = useState<Appearance>(appearanceProp ?? 'light');
  const [density, setDensityState] = useState<Density>(densityProp ?? 'comfortable');

  useEffect(() => {
    if (brandProp !== undefined) {
      setBrandState({ ...defaultBrand, ...brandProp });
    }
  }, [brandProp]);

  useEffect(() => {
    if (appearanceProp !== undefined) {
      setAppearanceState(appearanceProp);
    }
  }, [appearanceProp]);

  useEffect(() => {
    if (densityProp !== undefined) {
      setDensityState(densityProp);
    }
  }, [densityProp]);

  const setAppearance = useCallback((next: Appearance) => {
    setAppearanceState(next);
  }, []);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
  }, []);

  const setBrand = useCallback((next: BrandConfig) => {
    setBrandState(next);
  }, []);

  const theme = useMemo(
    () => createTheme(brand, appearance, density),
    [brand, appearance, density],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      appearance,
      density,
      setAppearance,
      setDensity,
      setBrand,
    }),
    [theme, appearance, density, setAppearance, setDensity, setBrand],
  );

  return (
    <ThemeContext.Provider value={value}>
      <LayerManagerProvider>
        <div data-ui-root style={theme.cssVars as React.CSSProperties}>
          {children}
        </div>
      </LayerManagerProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
